import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Building2, Upload, TrendingUp, Wallet,
  CreditCard, Landmark, PiggyBank, AlertCircle, CheckCircle2,
  FileText, Trash2, RefreshCw, ArrowUpRight, ArrowDownRight,
  Info, AlertTriangle, BookOpen, X, FileUp, ClipboardPaste
} from 'lucide-react'
import api from '../services/api'
import { useTheme } from '../hooks/useTheme'
import { useWorkspace } from '../hooks/useWorkspace'
import { 
  PieChart, Pie, Cell, 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Legend
} from 'recharts'

// Types
interface DebitoBanca {
  codice: string
  descrizione: string
  saldo: number
}

interface DashboardPatrimoniale {
  posizione_finanziaria: {
    totale_attivo: number
    totale_passivo: number
    patrimonio_netto: number
    posizione_netta: number
  }
  cash_position: {
    totale_disponibilita: number
    dettaglio_banche: DebitoBanca[]
    cassa_contanti: number
  }
  working_capital: {
    crediti_clienti: number
    debiti_fornitori: number
    capitale_circolante_netto: number
  }
  debiti_breakdown: {
    tributari: number
    previdenziali: number
    verso_dipendenti: number
    finanziari: number
    fornitori: number
    altri: number
    totale: number
  }
  kpi: {
    current_ratio: number
    quick_ratio: number
    debt_to_equity: number
    cash_ratio: number
  }
  conto_economico: {
    ricavi_totali: number
    costi_totali: number
    risultato_operativo: number
  }
  periodo: {
    inizio: string
    fine: string
  }
  azienda: string
  aggiornato_al: string
}

interface SituazioneSummary {
  id: string
  azienda: string
  periodo_inizio: string
  periodo_fine: string
  conti_count: number
  created_at: string
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount)
}

// Format date
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

// KPI Badge
function KPIBadge({ value, label, good, warning }: { value: number; label: string; good: number; warning: number }) {
  const isGood = value >= good
  const isWarning = value >= warning && value < good
  const isBad = value < warning
  
  return (
    <div className={`p-4 rounded-xl border ${
      isGood ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' :
      isWarning ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800' :
      'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
    }`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">{label}</span>
        {isGood && <CheckCircle2 className="w-4 h-4 text-green-600" />}
        {isWarning && <AlertTriangle className="w-4 h-4 text-yellow-600" />}
        {isBad && <AlertCircle className="w-4 h-4 text-red-600" />}
      </div>
      <div className={`kpi-value kpi-animate mt-1 ${
        isGood ? 'text-green-700 dark:text-green-400' :
        isWarning ? 'text-yellow-700 dark:text-yellow-400' :
        'text-red-700 dark:text-red-400'
      }`}>
        {value.toFixed(2)}
      </div>
    </div>
  )
}

export default function Patrimoniale() {
  const { isDark } = useTheme()
  const { currentWorkspace } = useWorkspace()
  const queryClient = useQueryClient()
  
  const [showUpload, setShowUpload] = useState(false)
  const [uploadText, setUploadText] = useState('')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadMode, setUploadMode] = useState<'file' | 'paste'>('file')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [showPianoContiConfirm, setShowPianoContiConfirm] = useState(false)
  
  // Fetch dashboard
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['patrimoniale-dashboard', currentWorkspace?.id],
    queryFn: async () => {
      const res = await api.get('/patrimoniale/dashboard')
      return res.data
    },
    enabled: !!currentWorkspace,
  })
  
  // Fetch situazioni list
  const { data: situazioniData } = useQuery({
    queryKey: ['patrimoniale-situazioni', currentWorkspace?.id],
    queryFn: async () => {
      const res = await api.get('/patrimoniale/situazione')
      return res.data
    },
    enabled: !!currentWorkspace,
  })

  // Fetch Piano dei Conti status
  const { data: pianoContiData } = useQuery({
    queryKey: ['piano-conti-stats', currentWorkspace?.id],
    queryFn: async () => {
      const res = await api.get('/patrimoniale/piano-conti/stats')
      return res.data
    },
    enabled: !!currentWorkspace,
  })
  
  // Upload mutation (testo incollato)
  const uploadMutation = useMutation({
    mutationFn: async (text: string) => {
      const res = await api.post('/patrimoniale/upload', { text })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patrimoniale-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['patrimoniale-situazioni'] })
      setShowUpload(false)
      setUploadText('')
      setUploadError(null)
    },
    onError: (err: Error) => {
      setUploadError(err.message || 'Errore durante il caricamento')
    },
  })

  // Upload mutation (file PDF/XLSX/CSV)
  const fileUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post('/patrimoniale/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['patrimoniale-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['patrimoniale-situazioni'] })
      setShowUpload(false)
      setSelectedFile(null)
      setUploadError(null)
      // Se asincrono (job in coda), mostriamo un messaggio
      if (data?.queued) {
        alert('File caricato! L\'elaborazione è in corso. Ricarica la pagina tra qualche secondo.')
      }
    },
    onError: (err: Error) => {
      setUploadError(err.message || 'Errore durante il caricamento del file')
    },
  })
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/patrimoniale/situazione/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patrimoniale-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['patrimoniale-situazioni'] })
    },
  })

  // Delete Piano dei Conti mutation
  const deletePianoContiMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/patrimoniale/piano-conti')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['piano-conti-stats'] })
      setShowPianoContiConfirm(false)
    },
  })
  
  const dashboard: DashboardPatrimoniale | null = dashboardData?.dashboard || null
  const situazioni: SituazioneSummary[] = situazioniData?.situazioni || []
  
  // Prepare chart data
  const debitiChartData = dashboard ? [
    { name: 'Tributari', value: Math.abs(dashboard.debiti_breakdown.tributari), color: '#ef4444' },
    { name: 'Previdenziali', value: Math.abs(dashboard.debiti_breakdown.previdenziali), color: '#f97316' },
    { name: 'Fornitori', value: Math.abs(dashboard.debiti_breakdown.fornitori), color: '#eab308' },
    { name: 'Finanziari', value: Math.abs(dashboard.debiti_breakdown.finanziari), color: '#22c55e' },
    { name: 'Dipendenti', value: Math.abs(dashboard.debiti_breakdown.verso_dipendenti), color: '#3b82f6' },
    { name: 'Altri', value: Math.abs(dashboard.debiti_breakdown.altri), color: '#8b5cf6' },
  ].filter(d => d.value > 0) : []
  
  const bancheChartData = dashboard?.cash_position.dettaglio_banche.map(b => ({
    name: b.descrizione.length > 20 ? b.descrizione.substring(0, 20) + '...' : b.descrizione,
    saldo: b.saldo,
  })) || []
  
  const axisColor = isDark ? '#9ca3af' : '#6b7280'
  
  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.type === 'text/plain') {
      const reader = new FileReader()
      reader.onload = (event) => {
        setUploadText(event.target?.result as string)
      }
      reader.readAsText(file)
    }
  }
  
  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text')
    if (text) {
      setUploadText(text)
    }
  }
  
  return (
    <div className="space-y-6 pt-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            Patrimoniale
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            Analisi della situazione contabile e indicatori finanziari
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-sm"
          >
            <Upload className="w-4 h-4" />
            Carica Situazione
          </button>
        </div>
      </div>
      
      {/* Upload Panel */}
      {showUpload && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Carica Situazione Contabile
          </h3>

          {/* Tab selector */}
          <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg p-1">
            <button
              onClick={() => setUploadMode('file')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                uploadMode === 'file'
                  ? 'bg-white dark:bg-gray-600 text-indigo-700 dark:text-indigo-300 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <FileUp className="w-4 h-4" />
              Carica File
            </button>
            <button
              onClick={() => setUploadMode('paste')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                uploadMode === 'paste'
                  ? 'bg-white dark:bg-gray-600 text-indigo-700 dark:text-indigo-300 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <ClipboardPaste className="w-4 h-4" />
              Copia-Incolla
            </button>
          </div>

          {/* File upload mode */}
          {uploadMode === 'file' && (
            <>
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-medium">Formati supportati: PDF, XLSX, XLS, CSV, TXT</p>
                  </div>
                </div>
              </div>
              <div
                className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors"
                onClick={() => document.getElementById('patrimoniale-file-input')?.click()}
                onDrop={(e) => {
                  e.preventDefault()
                  const file = e.dataTransfer.files[0]
                  if (file) setSelectedFile(file)
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                <input
                  id="patrimoniale-file-input"
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) setSelectedFile(file)
                  }}
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-indigo-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null) }}
                      className="ml-4 p-1 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">Clicca o trascina un file qui</p>
                    <p className="text-sm text-gray-400 mt-1">PDF, Excel, CSV o TXT</p>
                  </>
                )}
              </div>
            </>
          )}

          {/* Paste mode */}
          {uploadMode === 'paste' && (
            <>
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-medium mb-1">Come caricare la situazione contabile:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Apri il PDF della situazione contabile</li>
                      <li>Seleziona tutto il testo (Ctrl/Cmd + A)</li>
                      <li>Copia (Ctrl/Cmd + C)</li>
                      <li>Incolla qui sotto (Ctrl/Cmd + V)</li>
                    </ol>
                  </div>
                </div>
              </div>
              <div
                className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-6"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <textarea
                  value={uploadText}
                  onChange={(e) => setUploadText(e.target.value)}
                  onPaste={handlePaste}
                  placeholder="Incolla qui il testo della situazione contabile..."
                  className="w-full h-48 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 resize-none focus:outline-none"
                />
              </div>
            </>
          )}
          
          {uploadError && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <span className="text-red-700 dark:text-red-400">{uploadError}</span>
            </div>
          )}
          
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => {
                setShowUpload(false)
                setUploadText('')
                setUploadError(null)
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={() => {
                if (uploadMode === 'file' && selectedFile) {
                  fileUploadMutation.mutate(selectedFile)
                } else if (uploadMode === 'paste' && uploadText.trim()) {
                  uploadMutation.mutate(uploadText)
                }
              }}
              disabled={
                (uploadMode === 'file' ? !selectedFile : !uploadText.trim()) ||
                uploadMutation.isPending || fileUploadMutation.isPending
              }
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {(uploadMutation.isPending || fileUploadMutation.isPending) ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Analizza
            </button>
          </div>
        </div>
      )}
      
      {/* Loading */}
      {dashboardLoading && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      )}
      
      {/* Empty State */}
      {!dashboardLoading && !dashboard && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Nessuna situazione contabile
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Carica una situazione contabile per visualizzare la dashboard patrimoniale con KPI finanziari, 
            analisi della liquidità e breakdown dei debiti.
          </p>
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Upload className="w-5 h-5" />
            Carica Situazione Contabile
          </button>
        </div>
      )}
      
      {/* Dashboard */}
      {dashboard && (
        <>
          {/* Company & Period */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{dashboard.azienda}</h2>
                <p className="text-indigo-200 mt-1">
                  Periodo: {formatDate(dashboard.periodo.inizio)} - {formatDate(dashboard.periodo.fine)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-indigo-200 text-sm">Ultimo aggiornamento</p>
                <p className="text-sm font-medium">
                  {new Date(dashboard.aggiornato_al).toLocaleDateString('it-IT')}
                </p>
              </div>
            </div>
          </div>
          
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPIBadge value={dashboard.kpi.current_ratio} label="Current Ratio" good={1.5} warning={1.0} />
            <KPIBadge value={dashboard.kpi.quick_ratio} label="Quick Ratio" good={1.0} warning={0.7} />
            <KPIBadge value={dashboard.kpi.cash_ratio} label="Cash Ratio" good={0.5} warning={0.2} />
            <KPIBadge value={dashboard.kpi.debt_to_equity} label="Debt/Equity" good={0.5} warning={1.0} />
          </div>
          
          {/* Main Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cash Position */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <Wallet className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Disponibilità Liquide</h3>
                  <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                    {formatCurrency(dashboard.cash_position.totale_disponibilita)}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2 mt-4">
                {dashboard.cash_position.dettaglio_banche.slice(0, 4).map((banca, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400 truncate pr-2">
                      {banca.descrizione}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {formatCurrency(banca.saldo)}
                    </span>
                  </div>
                ))}
                {dashboard.cash_position.cassa_contanti > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Cassa contanti</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(dashboard.cash_position.cassa_contanti)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Working Capital */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Capitale Circolante Netto</h3>
                  <p className={`kpi-value kpi-animate ${
                    dashboard.working_capital.capitale_circolante_netto >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {formatCurrency(dashboard.working_capital.capitale_circolante_netto)}
                  </p>
                </div>
              </div>
              
              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Crediti clienti</span>
                  </div>
                  <span className="font-medium text-green-600">
                    {formatCurrency(dashboard.working_capital.crediti_clienti)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ArrowDownRight className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Debiti fornitori</span>
                  </div>
                  <span className="font-medium text-red-600">
                    {formatCurrency(dashboard.working_capital.debiti_fornitori)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Conto Economico */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <PiggyBank className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Risultato Operativo</h3>
                  <p className={`kpi-value kpi-animate ${
                    dashboard.conto_economico.risultato_operativo >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {formatCurrency(dashboard.conto_economico.risultato_operativo)}
                  </p>
                </div>
              </div>
              
              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Ricavi</span>
                  </div>
                  <span className="font-medium text-green-600">
                    {formatCurrency(dashboard.conto_economico.ricavi_totali)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ArrowDownRight className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Costi</span>
                  </div>
                  <span className="font-medium text-red-600">
                    {formatCurrency(dashboard.conto_economico.costi_totali)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Debiti Breakdown Pie */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-red-600" />
                Composizione Debiti
              </h3>
              
              {debitiChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={debitiChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {debitiChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: isDark ? '#1f2937' : '#ffffff',
                        borderColor: isDark ? '#374151' : '#e5e7eb',
                        borderRadius: '0.5rem',
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Nessun debito registrato
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Totale Debiti</span>
                  <span className="kpi-value kpi-animate text-red-600">
                    {formatCurrency(dashboard.debiti_breakdown.totale)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Banche Bar Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Landmark className="w-5 h-5 text-green-600" />
                Saldi Conti Bancari
              </h3>
              
              {bancheChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={bancheChartData} layout="vertical">
                    <XAxis type="number" tick={{ fill: axisColor }} tickFormatter={(v) => `€${v.toLocaleString()}`} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fill: axisColor, fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: isDark ? '#1f2937' : '#ffffff',
                        borderColor: isDark ? '#374151' : '#e5e7eb',
                        borderRadius: '0.5rem',
                      }}
                    />
                    <Bar dataKey="saldo" fill="#22c55e" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Nessun conto bancario
                </div>
              )}
            </div>
          </div>
          
          {/* Posizione Finanziaria */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Posizione Finanziaria
            </h3>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400 mb-1">Totale Attivo</p>
                <p className="kpi-value kpi-animate text-blue-600">
                  {formatCurrency(dashboard.posizione_finanziaria.totale_attivo)}
                </p>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400 mb-1">Totale Passivo</p>
                <p className="kpi-value kpi-animate text-red-600">
                  {formatCurrency(dashboard.posizione_finanziaria.totale_passivo)}
                </p>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400 mb-1">Patrimonio Netto</p>
                <p className="kpi-value kpi-animate text-purple-600">
                  {formatCurrency(dashboard.posizione_finanziaria.patrimonio_netto)}
                </p>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400 mb-1">Posizione Netta</p>
                <p className={`kpi-value kpi-animate ${
                  dashboard.posizione_finanziaria.posizione_netta >= 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {formatCurrency(dashboard.posizione_finanziaria.posizione_netta)}
                </p>
              </div>
            </div>
          </div>
          
          {/* Piano dei Conti */}
          {pianoContiData?.exists && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                  Piano dei Conti Ufficiale
                </h3>
                {!showPianoContiConfirm ? (
                  <button
                    onClick={() => setShowPianoContiConfirm(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Elimina
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-600 dark:text-red-400">Confermi?</span>
                    <button
                      onClick={() => deletePianoContiMutation.mutate()}
                      disabled={deletePianoContiMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {deletePianoContiMutation.isPending ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                      Sì, elimina
                    </button>
                    <button
                      onClick={() => setShowPianoContiConfirm(false)}
                      className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Voci totali</p>
                  <p className="text-sm font-bold text-indigo-600">{pianoContiData.stats?.totale_voci?.toLocaleString('it-IT')}</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Attivo (Sez. 1)</p>
                  <p className="text-sm font-bold text-blue-600">{pianoContiData.stats?.per_sezione?.[1] || 0}</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Passivo (Sez. 2)</p>
                  <p className="text-sm font-bold text-red-600">{pianoContiData.stats?.per_sezione?.[2] || 0}</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">CE (Sez. 3+4)</p>
                  <p className="text-sm font-bold text-green-600">{(pianoContiData.stats?.per_sezione?.[3] || 0) + (pianoContiData.stats?.per_sezione?.[4] || 0)}</p>
                </div>
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                Caricato il {pianoContiData.caricato_il ? new Date(pianoContiData.caricato_il).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </p>
            </div>
          )}

          {/* Situazioni List */}
          {situazioni.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-600" />
                Situazioni Caricate ({situazioni.length})
              </h3>
              
              <div className="space-y-3">
                {situazioni.map((sit) => (
                  <div 
                    key={sit.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{sit.azienda}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(sit.periodo_inizio)} - {formatDate(sit.periodo_fine)} • {sit.conti_count} conti
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('Sei sicuro di voler eliminare questa situazione?')) {
                          deleteMutation.mutate(sit.id)
                        }
                      }}
                      className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
